/**
 * navigation/linking.ts
 * Platform identity emails link back to the catalog SPA root with a
 * `?mode=…&token=…` query (see skplatform-api product registry). These are
 * query-based deep links (not path segments), so we parse them explicitly and
 * hand the root navigator the matching auth route — this keeps the existing
 * email-link contract intact with zero backend changes.
 */

import { Platform } from 'react-native';
import type { AuthStackParamList } from './types';
import { NOT_IN_PLAN_PATH } from './planGate';

export type AuthDeepLink =
  | { route: 'resetPassword'; params: { token: string } }
  | { route: 'verifyEmail'; params: { token: string } }
  | { route: 'forgotPassword'; params: undefined }
  | { route: 'checkEmail'; params: { email: string } }
  | { route: 'invitationAccept'; params: { token: string } }
  | { route: 'notInPlan'; params: { product: string } }
  | null;

/** Read `?mode=…&token=…` from the SPA root URL (web only). */
export function readAuthDeepLink(): AuthDeepLink {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const token = params.get('token') ?? '';
  if (mode === 'reset') return { route: 'resetPassword', params: { token } };
  if (mode === 'verify') return { route: 'verifyEmail', params: { token } };
  if (mode === 'forgot') return { route: 'forgotPassword', params: undefined };
  // Post-signup "check your inbox" screen. It has no session yet (or only an
  // unverified one), so without a URL a reload falls through to the boot probe
  // and bounces to login/dashboard. Encoding it as a deep link keeps the user
  // on this screen across refreshes (deep links win over the session probe).
  if (mode === 'check-email') return { route: 'checkEmail', params: { email: params.get('email') ?? '' } };
  // Product invite emails (crm/cal/people/queue/… — every redirect-handoff
  // product) land here. `email` / `org_name` are also on the URL for a
  // same-tab fallback, but the screen re-fetches both fresh via the preview
  // endpoint rather than trusting a possibly-stale query param.
  if (mode === 'accept-invite') return { route: 'invitationAccept', params: { token } };
  // Voice-product auth links use PATH routing (per the product registry), not
  // `?mode=`. A platform/voice-scoped signup (no product handoff) emails a
  // verify link to `/org-auth/business-info?token=…`; map it to the same
  // verify screen so it lands on Step 2 like the `?mode=verify` links do.
  if (token && window.location.pathname.includes('/org-auth/business-info')) {
    return { route: 'verifyEmail', params: { token } };
  }
  if (token && window.location.pathname.includes('/auth/reset-password')) {
    return { route: 'resetPassword', params: { token } };
  }
  // A product the org did not buy: the post-login handoff and the product
  // websites both send the user here. A deep link, so it wins over the session
  // probe — a signed-in user must see this page, not the platform shell.
  if (window.location.pathname.startsWith(NOT_IN_PLAN_PATH)) {
    return { route: 'notInPlan', params: { product: params.get('product') ?? '' } };
  }
  return null;
}

/**
 * The admin's upgrade-request notification (CC-01 §5.9, UICC B9) links to
 * `?billing_request=<id>&billing_product=<code>`. Not `product`: that name
 * belongs to the product-handoff contract. Opens the shell on Plan & Usage,
 * which opens the drawer's request inbox.
 */
export function readBillingRequestLink(): { request: string; product: string } | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  const request = q.get('billing_request');
  const product = q.get('billing_product');
  return request && product ? { request, product } : null;
}

/**
 * Signup intent from a product "Start free" CTA (`?mode=signup`). This is NOT a
 * deep link — it must NOT bypass the session probe. It only chooses the
 * UNAUTHENTICATED entry screen (signup instead of the default login); an existing
 * session still wins and the user is sent to the dashboard / product.
 */
export function readSignupIntent(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('mode') === 'signup';
}

/**
 * A product just signed the user out and sent them here (`?signed_out=1`).
 *
 * The boot probe (GET /v1/me) is pointless then: the session was ended a moment
 * ago, so it can only answer 401, and the 401 then sets off a refresh that also
 * answers 401 — two failing calls on every logout (staging, 14 Oct). Seen here,
 * the login form opens straight away.
 *
 * The flag is removed from the address bar once read (the product handoff
 * params stay), so a later reload probes as usual and a still-valid session —
 * from another tab — is honoured.
 * Example: ?product=salon&return_to=…&state=…&signed_out=1 → login, URL keeps
 * product, return_to and state.
 */
export function readSignedOut(): boolean {
  /*
   * ONE ANSWER PER PAGE LOAD. Two places ask at boot — the navigator's probe and
   * the SessionProvider's first load — and the first to ask strips the flag from
   * the URL. Without the memo the second asker no longer saw it and called /me
   * anyway (dev, 14 Oct: the probe was skipped, the provider still fired).
   */
  if (_signedOut !== null) return _signedOut;
  _signedOut = false;
  if (Platform.OS !== 'web' || typeof window === 'undefined') return _signedOut;
  const url = new URL(window.location.href);
  if (url.searchParams.get('signed_out') !== '1') return _signedOut;
  url.searchParams.delete('signed_out');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  _signedOut = true;
  return _signedOut;
}

let _signedOut: boolean | null = null;

/** Tests only: forget the per-load answer. */
export function resetSignedOutForTests(): void {
  _signedOut = null;
}

/**
 * Pin the SPA URL to the post-signup check-email screen so a reload reopens it
 * (web only). Mirrors the `?mode=…` contract used by the platform email links.
 */
export function setCheckEmailDeepLink(email: string): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const q = `?mode=check-email&email=${encodeURIComponent(email)}`;
    window.history.replaceState({}, '', `${window.location.pathname}${q}`);
  }
}

/**
 * Drop the `?mode/?token` query after a deep-linked flow finishes, so a reload
 * doesn't re-open it. Call right before navigating back to login.
 */
export function clearAuthDeepLink(): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.history.replaceState({}, '', window.location.pathname);
  }
}

/** Narrowing helper so callers don't repeat the keyof cast. */
export type AuthRouteName = keyof AuthStackParamList;
